// ============================================================
// Advertise Me â€” pages
// ============================================================
import React, { useState as useStateP, useEffect as useEffectP, useMemo as useMemoP, useRef as useRefP } from 'react';
import { AM_DATA } from './data.js';
import { ScrollReveal, CursorParallax, KineticHero, SignageWall, ProductPreview, Stat, Page } from './components.jsx';

function useMarquee(ref) {
  useEffectP(() => {
    const track = ref.current;
    if (!track) return;
    const section = track.parentElement;
    let pos = 0;
    let speed = 0.6;
    let targetSpeed = 0.6;
    let raf;
    const BASE = 0.6, SLOW = 0.18;
    const tick = () => {
      targetSpeed = section.matches(':hover') ? SLOW : BASE;
      speed += (targetSpeed - speed) * 0.04;
      pos -= speed;
      const half = track.scrollWidth / 2;
      if (Math.abs(pos) >= half) pos = 0;
      track.style.transform = 'translateX(' + pos + 'px)';
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
}

// ---------------- Scroll Parallax Hook ----------------
function useScrollParallax(ref, speed = 0.3) {
  useEffectP(() => {
    if (typeof window === 'undefined') return;
    const el = ref.current;
    if (!el) return;
    let raf;
    const tick = () => {
      const rect = el.getBoundingClientRect();
      const viewH = window.innerHeight;
      const progress = (viewH - rect.top) / (viewH + rect.height);
      const clamped = Math.max(0, Math.min(1, progress));
      const offset = (clamped - 0.5) * speed * 200;
      el.style.setProperty('--plx', offset + 'px');
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [speed]);
}

// ---------------- Product Gallery ----------------
function ProductGallery({ filterCaptions }) {
  const data = AM_DATA;
  const modules = import.meta.glob('/public/images/portfolio/processed/*.{jpg,jpeg,png,webp}', { eager: true, query: '?url', import: 'default' });
  const folderItems = Object.entries(modules).map(([path, url]) => {
    const file = path.split('/').pop().replace(/\.[^.]+$/, '');
    const orientation = file.endsWith('-portrait') ? 'portrait' : 'landscape';
    const caption = file.replace(/-(landscape|portrait)(-\d+)?$/, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return { img: url.replace(/^\/public\//, '/'), caption, orientation };
  }).sort((a, b) => a.caption.localeCompare(b.caption));

  const source = folderItems.length ? folderItems : data.portfolio;
  const filterSet = new Set((filterCaptions || []).map(c => c.toLowerCase()));
  const filtered = filterSet.size > 0
    ? source.filter(item => filterSet.has(item.caption.toLowerCase()))
    : source.slice(0, 4);
  const seen = new Set();
  const deduped = filtered.filter(item => seen.has(item.caption) ? false : seen.add(item.caption));
  const items = deduped.length >= 2 ? deduped : source.slice(0, 4);

  const [lightbox, setLightbox] = useStateP(null);
  useEffectP(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const hue = (h) => `linear-gradient(135deg, hsl(${h}, 55%, 42%), hsl(${h + 30}, 45%, 28%))`;

  return (
    <React.Fragment>
      <div className="pd-gallery-grid">
        {items.map((item, i) => (
          <div
            key={i}
            className={`pd-gallery-item pd-gallery-item--${item.orientation}`}
            onClick={() => setLightbox(item)}
          >
            {item.img ? (
              <div className="pd-gallery-img">
                <img src={item.img} alt={item.caption} />
                <span className="pd-gallery-caption">{item.caption}</span>
              </div>
            ) : (
              <div className="pd-gallery-img pd-gallery-img--placeholder" style={{ background: hue(item.hue) }}>
                <span className="pd-gallery-orient">{item.orientation === 'landscape' ? 'â–­' : 'â–¯'}</span>
                <span className="pd-gallery-caption">{item.caption}</span>
              </div>
            )}
          </div>
        ))}
      </div>
      <div
        className={`portfolio-lightbox ${lightbox ? 'open' : ''}`}
        onClick={() => setLightbox(null)}
      >
        {lightbox && (
          <div className={`portfolio-lightbox-inner portfolio-lightbox-inner--${lightbox.orientation}`}>
            {lightbox.img ? (
              <img src={lightbox.img} alt={lightbox.caption} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', borderRadius: 8 }} />
            ) : (
              <div className="portfolio-placeholder" style={{ background: hue(lightbox.hue), width: '100%', height: '100%' }}>
                <span className="portfolio-orient">{lightbox.orientation === 'landscape' ? 'â–­ landscape' : 'â–¯ portrait'}</span>
              </div>
            )}
            <div className="portfolio-lightbox-caption">{lightbox.caption}</div>
            <div className="portfolio-lightbox-close">ESC to close</div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
}

// ---------------- Home ----------------

function PortfolioGallery() {
  const data = AM_DATA;
  const modules = import.meta.glob('/public/images/portfolio/processed/*.{jpg,jpeg,png,webp}', {
    eager: true, query: '?url', import: 'default'
  });

  const folderItems = Object.entries(modules).map(([path, url]) => {
    const file = path.split('/').pop().replace(/\.[^.]+$/, '');
    const orientation = file.endsWith('-portrait') || file.includes('-portrait-') ? 'portrait' : 'landscape';
    const caption = file
      .replace(/-portrait(-\d+)?$/, '')
      .replace(/-landscape(-\d+)?$/, '')
      .replace(/-/g, ' ');
    return { img: url, caption, orientation };
  }).sort((a, b) => a.caption.localeCompare(b.caption));

  const source = folderItems.length ? folderItems : data.portfolio;

  // Interleave: 2 landscape, 1 portrait
  const landscapes = source.filter(i => i.orientation !== 'portrait');
  const portraits = source.filter(i => i.orientation === 'portrait');
  const mixed = [];
  let li = 0, pi = 0;
  while (li < landscapes.length || pi < portraits.length) {
    if (li < landscapes.length) mixed.push(landscapes[li++]);
    if (li < landscapes.length) mixed.push(landscapes[li++]);
    if (pi < portraits.length) mixed.push(portraits[pi++]);
  }
  const ordered = mixed.length ? mixed : source;
  const items = [...ordered, ...ordered];

  const trackRef = useRefP(null);
  const [lightbox, setLightbox] = useStateP(null);

  // ESC closes lightbox
  useEffectP(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Auto-scroll RAF loop
  useEffectP(() => {
    const track = trackRef.current;
    if (!track) return;
    const section = track.parentElement;
    let pos = 0, speed = 0.5, targetSpeed = 0.5, raf;
    const BASE = 0.5, SLOW = 0;
    const tick = () => {
      targetSpeed = section.matches(':hover') ? SLOW : BASE;
      speed += (targetSpeed - speed) * 0.06;
      pos -= speed;
      const half = track.scrollWidth / 2;
      if (Math.abs(pos) >= half) pos = 0;
      track.style.transform = 'translateX(' + pos + 'px)';
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // 3D tilt handlers
  const onTilt = (e) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    el.style.transform = 'perspective(600px) rotateY(' + ((x - 0.5) * 8) + 'deg) rotateX(' + ((0.5 - y) * 6) + 'deg) scale(1.02)';
  };
  const onTiltReset = (e) => { e.currentTarget.style.transform = ''; };

  return (
    <React.Fragment>
      <div className="portfolio">
        <div className="portfolio-label">
          <span className="eyebrow"><span className="dot"></span>Our work</span>
        </div>
        <div className="portfolio-track" ref={trackRef}>
          {items.map((item, idx) => (
            <div
              key={idx}
              className={'pg-card pg-card--' + item.orientation}
              onMouseMove={onTilt}
              onMouseLeave={onTiltReset}
              onClick={() => setLightbox(item)}
            >
              <div className="pg-screen">
                {item.img ? (
                  <img src={item.img} alt={item.caption} loading="lazy" draggable="false" />
                ) : (
                  <div className="pg-placeholder" />
                )}
              </div>
              <div className="pg-caption">{item.caption}</div>
            </div>
          ))}
        </div>
      </div>

      {lightbox && (
        <div className="portfolio-lightbox open" onClick={() => setLightbox(null)}>
          <div className="portfolio-lightbox-inner" onClick={e => e.stopPropagation()}>
            <img src={lightbox.img} alt={lightbox.caption} />
            <div className="portfolio-lightbox-cap">{lightbox.caption}</div>
            <button className="portfolio-lightbox-close" onClick={() => setLightbox(null)}>Ã—</button>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

function HomePage({ navigate, heroMode }) {
  const data = AM_DATA;
  const marqueeRef = useRefP(null);
  useMarquee(marqueeRef);
  return (
    <Page name="home">
      <KineticHero
        mode={heroMode}
        eyebrow="Australian-made digital solutions since 2009"
        subtext="Custom digital signage, interactive touch solutions, wayfinding systems and directory boards for medical centres, shopping centres and hospitals. Designed, built and supported in Sydney, Australia."
        ctaPrimary="Get a free quote"
        metaBar={['Interactive Touch Solutions', 'Wayfinding & directories', 'Medical Centre Digital Signage', 'Bed management', 'ePosters', 'Custom Digital Signage']}
      />

      {/* Marquee logo strip */}
      <section className="marquee" data-section="â€” clients" aria-label="clients">
        <div className="marquee-track" ref={marqueeRef}>
          {[...data.clients, ...data.clients].map((c, i) =>
          <span key={i} className="logo-strip">
              <span>{c}</span>
              <span className="sep" />
            </span>
          )}
        </div>
      </section>

      {/* Animated signage wall */}
      <section className="section section--scene" data-section="â€” a wall of screens" style={{ position: 'relative' }}>
        <div className="container">
          <div className="section-head">
            <div>
              <div className="eyebrow"><span className="dot"></span>What we build</div>
              <h2 className="h1" style={{ marginTop: 16, fontSize: "50px" }}>
                One platform to manage every <em style={{ fontFamily: "'Inter', sans-serif", fontStyle: 'italic', color: 'var(--accent)', letterSpacing: '-0.025em', fontSize: "54px", fontWeight: "500" }}>screen</em><br />
                and every function.
              </h2>
            </div>
            <p className="lede">
              Rosters, directories, wayfinding, interactive kiosks â€” running live, right now, in medical centres, hospitals, malls and universities across the country. These aren't templates. They're conceptual wireframes we've built into real products for our clients.
            </p>
          </div>
        </div>
        <SignageWall />
      </section>

      {/* Products grid */}
      <section id="what-we-deploy" className="section section--scene" data-section="â€” flagship products" style={{ position: 'relative' }}>
        <div className="container">
          <div className="section-head">
            <div>
              <div className="eyebrow"><span className="dot"></span>What we deploy</div>
              <h2 className="h1" style={{ marginTop: 16 }}>Purpose-built. Not <span style={{ color: 'rgb(0, 102, 255)' }}>off-the-shelf.</span></h2>
            </div>
            <p className="lede">
              Each solution is designed for a specific environment and a specific problem. No generic templates, no DIY dashboards. We configure it, implement it, and keep it running.
            </p>
          </div>
          <ScrollReveal className="products-grid" stagger>
            {data.products.map((p, i) =>
            <div key={p.slug} className="product-card" onClick={() => navigate(`/products/${p.slug}`)}>
                <div className="product-card-tag">
                  <span className="product-card-num mono">0{i + 1} Â· flagship</span>
                  <span className="tag tag--live"><span className="dot"></span>live</span>
                </div>
                <h3 className="product-card-title">{p.name}</h3>
                <p className="product-card-desc">{p.summary}</p>
                <div className="product-card-preview">
                  <ProductPreview slug={p.slug} />
                </div>
                <div className="product-card-foot">
                  <span className="read">
                    Read more
                    <svg width="14" height="14" viewBox="0 0 16 16"><path d="M3 8H13M8 3L13 8L8 13" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
                  </span>
                  <span className="mono" style={{ color: 'var(--muted)' }}>{p.stat.v} {p.stat.l}</span>
                </div>
              </div>
            )}
          </ScrollReveal>
        </div>
      </section>

      {/* Industries band */}
      <section className="section section--tight" data-section="â€” industries" style={{ position: 'relative', paddingBottom: 0 }}>
        <div className="container">
          <div className="section-head">
            <div>
              <div className="eyebrow"><span className="dot"></span>Industries</div>
              <h2 className="h2" style={{ marginTop: 16 }}>Deployed in every kind of building.</h2>
            </div>
            <p className="lede">
              From a single screen in a GP clinic to a fleet of kiosks across a university or TAFE campus. We scale to fit.
            </p>
          </div>
        </div>
        <div className="container" style={{ padding: 0, maxWidth: 'var(--max)' }}>
          <ScrollReveal className="industries-grid" stagger>
            {data.industries.map((ind, i) =>
            <div key={ind.name} className="industry" onClick={() => {
                const slugMap = { 'Medical Centres': 'medical-centres', 'Shopping Centres': 'shopping-centres', 'Universities': 'education-research', 'Hospitals': 'hospitals' };
                navigate('/industries/' + (slugMap[ind.name] || ''));
              }}>
                <div className="industry-name">{ind.name}</div>
                <div className="industry-desc">{ind.desc}</div>
              </div>
            )}
          </ScrollReveal>
        </div>
        <PortfolioGallery />
      </section>

      {/* How it works */}
      <section className="section section--scene" data-section="â€” portfolio" />
      <section className="section section--scene" data-section="â€” process" style={{ position: 'relative' }}>
        <div className="container">
          <div className="section-head">
            <div>
              <div className="eyebrow"><span className="dot"></span>Process</div>
              <h2 className="h1" style={{ marginTop: 16 }}>From first conversation to live screen.</h2>
            </div>
            <p className="lede">
              No off-the-shelf templates. We design, build, deploy and support
              every screen ourselves â€” from the first sketch to the last cable.
            </p>
          </div>
          <ScrollReveal className="how" stagger>
            <div className="how-step">
              <div className="how-num">01 â€” Connect</div>
              <div className="how-title">Tell us what you need</div>
              <div className="how-desc">Send us a brief or drop us a line. We'll get back to you by email, then set up a call or online meeting when you're ready.</div>
            </div>
            <div className="how-step">
              <div className="how-num">02 â€” Design</div>
              <div className="how-title">Brand-fit interface</div>
              <div className="how-desc">Custom UI mockups within two weeks. We iterate until it's exactly right.</div>
            </div>
            <div className="how-step">
              <div className="how-num">03 â€” Deploy</div>
              <div className="how-title">Configure + prepare</div>
              <div className="how-desc">We configure the software, prepare the hardware and provide all the instructions and support your team needs for a smooth install.</div>
            </div>
            <div className="how-step">
              <div className="how-num">04 â€” Support</div>
              <div className="how-title">Local support from Sydney</div>
              <div className="how-desc">Local support via email and phone. Content updates in seconds. We're here when you need us.</div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA block */}
      <section className="section section--tight" data-section="â€” get in touch">
        <div className="container">
          <ScrollReveal>
            <div className="cta-block">
              <div>
                <div className="eyebrow" style={{ marginBottom: 18 }}><span className="dot"></span>Ready when you are</div>
                <h2 className="h1">
                  Your screens can do more<br />
                  than you think.
                </h2>
                <p className="lede" style={{ marginTop: 20 }}>
                  Tell us what you need. We'll come back with a custom quote and
                  mockups within 48 hours.
                </p>
              </div>
              <div className="cta-block-actions">
                <a data-link href="/quote" className="btn btn--accent btn--lg">
                  Get a quote
                  <span className="arrow"><svg viewBox="0 0 16 16" width="14" height="14"><path d="M4 12 L12 4 M6 4 H12 V10" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" /></svg></span>
                </a>
                <button
                  className="btn btn--ghost btn--lg"
                  onClick={() => document.getElementById('what-we-deploy').scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >See what we build</button>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </Page>);

}

// ================================================================
// Product detail page
// ================================================================
function ProductPage({ slug, navigate }) {
  const data = AM_DATA;
  const p = data.products.find((x) => x.slug === slug);
  if (!p) {
    return <Page name="404"><div className="container" style={{ paddingTop: 120 }}><h1 className="h1">Not found</h1></div></Page>;
  }
  const pg = p.page || {};
  const related = data.products.filter((x) => x.slug !== slug).slice(0, 3);
  const idx = data.products.findIndex((x) => x.slug === slug);
  const plxRef1 = useRefP(null);
  const plxRef2 = useRefP(null);
  useScrollParallax(plxRef1, 0.25);
  useScrollParallax(plxRef2, 0.15);

  return (
    <Page name="product">

      {/* ---- HERO ---- */}
      <section className="pd-hero">
        <div className="pd-hero-bg" />
        <div className="container">
          <div className="pd-back" onClick={() => navigate('/')}>
            <svg width="14" height="14" viewBox="0 0 16 16" style={{ marginRight: 6 }}><path d="M13 8H3M8 3L3 8L8 13" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
            All products
          </div>
          <div className="pd-hero-inner">
            <div className="pd-hero-content">
              <div className="pd-badges">
                <span className="pd-badge pd-badge--cloud">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" /></svg>
                  {pg.cloudLabel || 'Cloud managed'}
                </span>
                <span className="pd-badge">
                  <span className="dot" style={{ background: '#22c55e' }}></span>
                  Live product
                </span>
              </div>
              <h1 className="pd-name">{p.name}</h1>
              <p className="pd-headline">{pg.headline || p.summary}</p>
              <p className="pd-summary">{pg.heroDesc || p.summary}</p>
              <div className="pd-stat-row">
                <div className="pd-stat"><Stat v={p.stat.v} l={p.stat.l} /></div>
                <div className="pd-stat"><Stat v="< 48h" l="quote turnaround" /></div>
              </div>
              <div className="pd-hero-actions">
                <a data-link href="/quote" className="btn btn--accent btn--lg">
                  Get a quote
                  <span className="arrow"><svg viewBox="0 0 16 16" width="14" height="14"><path d="M4 12 L12 4 M6 4 H12 V10" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" /></svg></span>
                </a>
                <a href="tel:1300880005" className="btn btn--ghost btn--lg">
                  Call 1300 880 005
                </a>
              </div>
            </div>
            <CursorParallax strength={10}>
              <div className="pd-preview-large">
                <ProductPreview slug={p.slug} />
              </div>
            </CursorParallax>
          </div>
        </div>
      </section>

      {/* ---- WHAT IS IT ---- */}
      {pg.whatIsIt && (
        <section className="pd-section pd-what" ref={plxRef1}>
          <div className="pd-plx-bg" style={{ transform: 'translateY(var(--plx, 0px))' }} />
          <div className="container">
            <ScrollReveal>
              <div className="pd-what-inner">
                <div className="pd-what-left">
                       {p.images && p.images[0] && <img src={p.images[0]} alt="" className="pd-what-bg" />}
                  <div className="eyebrow"><span className="dot"></span>The product</div>
                  <h2 className="pd-section-title">{pg.whatIsIt.title}</h2>
                </div>
                <div>
                  <p className="pd-what-body">{pg.whatIsIt.body}</p>
                  <div className="pd-what-highlight">
                    <span className="pd-what-highlight-bar" />
                    {pg.whatIsIt.highlight}
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* ---- BENEFITS ---- */}
      {pg.benefits && (
        <section className="pd-section">
          <div className="container">
            <ScrollReveal>
              <div className="eyebrow"><span className="dot"></span>Why it matters</div>
              <h2 className="pd-section-title" style={{ marginBottom: 48 }}>Benefits that change how you operate.</h2>
            </ScrollReveal>
            <ScrollReveal className="pd-benefits" stagger>
              {pg.benefits.map((b, i) => (
                <div key={i} className="pd-benefit">
                  <div className="pd-benefit-icon">{b.icon}</div>
                  <h3 className="pd-benefit-title">{b.title}</h3>
                  <p className="pd-benefit-desc">{b.desc}</p>
                </div>
              ))}
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* ---- CAPABILITIES + CUSTOMISATION ---- */}
      <section className="pd-section pd-section--alt" ref={plxRef2}>
        <div className="container">
          <div className="pd-caps-cust">
            <div className="pd-caps-col">
              <ScrollReveal>
                <div className="eyebrow"><span className="dot"></span>Capabilities</div>
                <h2 className="pd-caps-cust-title">What ships with every deployment.</h2>
                {pg.featuresDesc && <p className="pd-cust-desc">{pg.featuresDesc}</p>}
              </ScrollReveal>
              <ScrollReveal stagger>
                {p.features.map((f, i) => (
                  <div key={i} className="pd-caps-item">
                    <div className="pd-caps-item-num mono">0{i + 1}</div>
                    <div>
                      <div className="pd-caps-item-t">{f.t}</div>
                      <div className="pd-caps-item-d">{f.d}</div>
                    </div>
                  </div>
                ))}
              </ScrollReveal>
            </div>
            {pg.customisation && (
              <div className="pd-cust-col">
                <ScrollReveal>
                  <div className="eyebrow"><span className="dot"></span>Customisation</div>
                  <h2 className="pd-caps-cust-title">{pg.customisation.title}</h2>
                  <p className="pd-cust-desc">{pg.customisation.desc}</p>
                </ScrollReveal>
                <ScrollReveal stagger>
                  {pg.customisation.points.map((pt, i) => (
                    <div key={i} className="pd-cust-item">
                      <div className="pd-cust-item-num mono">0{i + 1}</div>
                      <div>
                        <h3 className="pd-cust-item-t">{pt.t}</h3>
                        <p className="pd-cust-item-d">{pt.d}</p>
                      </div>
                    </div>
                  ))}
                </ScrollReveal>
              </div>
            )}
            {pg.integration && (
              <div className="pd-integ-row">
                <ScrollReveal>
                  <div className="pd-integ-row-head">
                    <div>
                      <div className="eyebrow"><span className="dot"></span>Integration</div>
                      <h2 className="pd-caps-cust-title">{pg.integration.title}</h2>
                      <p className="pd-cust-desc">{pg.integration.desc}</p>
                    </div>
                    <div className="pd-integ-chips">
                      {pg.integration.points.map((pt, i) => (
                        <span key={i} className="pd-integ-chip">
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}><circle cx="10" cy="10" r="10" fill="var(--accent)" opacity="0.15" /><path d="M6 10.5L8.5 13L14 7" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                          {pt}
                        </span>
                      ))}
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ---- PRICING ---- */}
      <section className="pd-section">
        <div className="container">
          <ScrollReveal>
            <div className="pd-pricing-block">
              <div className="pd-pricing-content">
                <div className="eyebrow"><span className="dot"></span>Pricing</div>
                <h2 className="pd-section-title">Priced to your requirements. Not a fixed plan.</h2>
                <p className="pd-pricing-body">
                  Every project is different, so every quote is tailored. Pricing is based on the number of screens, 
                  the complexity of your interface, the integrations you need and the level of ongoing support. 
                  We provide a transparent, itemised quote after understanding your requirements. No hidden fees, 
                  no lock-in contracts, no surprises.
                </p>
                <div className="pd-pricing-points">
                  <div className="pd-pricing-point">
                    <span className="pd-pricing-point-icon">ðŸ’¬</span>
                    <div>
                      <strong>Free consultation</strong>
                      <p>Tell us what you need. We'll scope it and come back with a clear, itemised quote within 48 hours.</p>
                    </div>
                  </div>
                  <div className="pd-pricing-point">
                    <span className="pd-pricing-point-icon">ðŸ“</span>
                    <div>
                      <strong>Concept mockups included</strong>
                      <p>Every quote comes with concept wireframes so you can see what you are getting before you commit.</p>
                    </div>
                  </div>
                  <div className="pd-pricing-point">
                    <span className="pd-pricing-point-icon">ðŸ”„</span>
                    <div>
                      <strong>Flexible engagement</strong>
                      <p>One-off projects, ongoing support contracts, or a combination. We structure the engagement to suit you.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="pd-pricing-cta">
                <div className="pd-pricing-cta-inner">
                  <div className="pd-pricing-cta-label">Ready for a quote?</div>
                  <div className="pd-pricing-cta-sub">
                    Tell us about your project and we will send you a tailored quote with concept mockups within 48 hours.
                  </div>
                  <a data-link href="/quote" className="btn btn--accent" style={{ marginTop: 20 }}>
                    Get a quote
                    <span className="arrow"><svg viewBox="0 0 16 16" width="14" height="14"><path d="M4 12 L12 4 M6 4 H12 V10" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" /></svg></span>
                  </a>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ---- LOCAL SUPPORT ---- */}
      <section className="pd-section pd-section--alt">
        <div className="container">
          <ScrollReveal>
            <div className="eyebrow"><span className="dot"></span>Support</div>
            <h2 className="pd-section-title" style={{ marginBottom: 48 }}>Local support from Sydney. Always.</h2>
          </ScrollReveal>
          <ScrollReveal className="pd-support-grid" stagger>
            <div className="pd-support-card">
              <div className="pd-support-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7L12 13L2 7" /></svg>
              </div>
              <h3 className="pd-support-title">Email support</h3>
              <p className="pd-support-desc">Send us a support request any time. We respond within 4 hours during business hours, often sooner.</p>
              <a href="mailto:hello@advertiseme.au" className="pd-support-link">hello@advertiseme.au</a>
            </div>
            <div className="pd-support-card">
              <div className="pd-support-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
              </div>
              <h3 className="pd-support-title">Phone support</h3>
              <p className="pd-support-desc">Call our Sydney office directly. We are a small team so if we miss you, we will call back the same day.</p>
              <a href="tel:1300880005" className="pd-support-link">1300 880 005</a>
            </div>
            <div className="pd-support-card">
              <div className="pd-support-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
              </div>
              <h3 className="pd-support-title">Remote support</h3>
              <p className="pd-support-desc">We connect to your screens remotely to diagnose issues, push updates and make changes without an on-site visit.</p>
              <span className="pd-support-link">Included with every deployment</span>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ---- GALLERY ---- */}
      <section className="pd-section">
        <div className="container">
          <ScrollReveal>
            <div className="eyebrow"><span className="dot"></span>In the field</div>
            <h2 className="pd-section-title" style={{ marginBottom: 12 }}>Real deployments. Real clients.</h2>
            <p className="pd-gallery-sub">Every photo below is a live installation from one of our clients across Australia.</p>
          </ScrollReveal>
          <ScrollReveal>
            <ProductGallery filterCaptions={pg.galleryFilter} />
          </ScrollReveal>
        </div>
      </section>

      {/* ---- DEMO PLACEHOLDER ---- */}
      <section className="pd-section pd-section--alt">
        <div className="container">
          <ScrollReveal>
            <div className="pd-demo-block">
              <div className="pd-demo-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              </div>
              <h2 className="pd-section-title">Interactive demo coming soon.</h2>
              <p className="pd-demo-desc">
                We are building a hands-on interactive demo so you can experience {p.name.toLowerCase()} directly in your browser. 
                In the meantime, get in touch and we will walk you through a live demo over a call.
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <a data-link href="/quote" className="btn btn--accent">
                  Request a live demo
                  <span className="arrow"><svg viewBox="0 0 16 16" width="14" height="14"><path d="M4 12 L12 4 M6 4 H12 V10" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" /></svg></span>
                </a>
                <a href="tel:1300880005" className="btn btn--ghost">Call us</a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ---- FINAL CTA ---- */}
      <section className="pd-section">
        <div className="container">
          <ScrollReveal>
            <div className="pd-final-cta">
              <div className="eyebrow"><span className="dot"></span>Next step</div>
              <h2 className="h1" style={{ marginTop: 16 }}>
                Ready to see what {p.name.toLowerCase()} can do for your organisation?
              </h2>
              <p className="lede" style={{ marginTop: 20, maxWidth: '56ch' }}>
                Tell us about your project. We will come back with a tailored quote, concept mockups and a clear scope within 48 hours.
              </p>
              <div className="pd-hero-actions" style={{ marginTop: 32 }}>
                <a data-link href="/quote" className="btn btn--accent btn--lg">
                  Get a quote
                  <span className="arrow"><svg viewBox="0 0 16 16" width="14" height="14"><path d="M4 12 L12 4 M6 4 H12 V10" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" /></svg></span>
                </a>
                <a href="mailto:hello@advertiseme.au" className="btn btn--ghost btn--lg">Email us</a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ---- RELATED ---- */}
      <section className="pd-section pd-section--alt" style={{ paddingBottom: 0 }}>
        <div className="container">
          <div className="eyebrow"><span className="dot"></span>Also explore</div>
          <h2 className="pd-section-title" style={{ marginBottom: 32 }}>Other products in our lineup.</h2>
        </div>
        <div className="container" style={{ padding: 0, maxWidth: 'var(--max)' }}>
          <div className="related">
            {related.map((r, i) =>
            <div key={r.slug} className="related-card" onClick={() => navigate(`/products/${r.slug}`)}>
                <div className="related-num mono">0{data.products.findIndex((x) => x.slug === r.slug) + 1}</div>
                <div className="related-name">{r.name}</div>
                <div className="related-summary">{r.summary}</div>
                <span className="related-arrow">
                  Read more
                  <svg width="14" height="14" viewBox="0 0 16 16"><path d="M3 8H13M8 3L13 8L8 13" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
                </span>
              </div>
            )}
          </div>
        </div>
      </section>
    </Page>);
}

// ================================================================
// Industries index page
// ================================================================
function IndustriesPage({ navigate }) {
  const data = AM_DATA;
  const industries = data.industryPages || [];

  return (
    <Page name="industries">
      <section className="about-hero">
        <div className="container">
          <div className="eyebrow"><span className="dot"></span>Industries</div>
          <h1 className="h1" style={{ marginTop: 16, maxWidth: '20ch' }}>
            Built for spaces where <em style={{ fontFamily: "'Inter', sans-serif", fontStyle: 'italic', fontWeight: 300, color: 'var(--accent)', letterSpacing: '-0.025em' }}>information moves fast.</em>
          </h1>
          <p className="lede" style={{ marginTop: 24, maxWidth: '60ch' }}>
            From a single-room GP to a 40-screen mall directory, our platform
            scales without changing how your team works. Choose your industry
            to see how we solve its specific problems.
          </p>
        </div>
      </section>

      <div className="container" style={{ paddingBottom: 'clamp(60px, 7vw, 100px)' }}>
        <ScrollReveal className="ind-index-grid" stagger>
          {industries.map((ind, i) => (
            <div key={ind.slug} className="ind-index-card" style={{"--ind-bg": "url(/images/industries/" + ind.slug + ".jpg)"}} onClick={() => navigate(`/industries/${ind.slug}`)}>
              <div className="ind-index-card-bg" />
              <div className="ind-index-card-top">
                <span className="ind-index-card-num mono">0{i + 1}</span>
              </div>
              <h2 className="ind-index-card-name">{ind.name}</h2>
              <p className="ind-index-card-desc">{ind.short}</p>
              <span className="related-arrow">
                Explore {ind.name.toLowerCase()}
                <svg width="14" height="14" viewBox="0 0 16 16"><path d="M3 8H13M8 3L13 8L8 13" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
              </span>
            </div>
          ))}
        </ScrollReveal>
      </div>
    </Page>);
}

// ================================================================
// Industry detail page
// ================================================================
function IndustryPage({ slug, navigate }) {
  const data = AM_DATA;
  const ind = (data.industryPages || []).find((x) => x.slug === slug);
  if (!ind) {
    return <Page name="404"><div className="container" style={{ paddingTop: 120 }}><h1 className="h1">Not found</h1></div></Page>;
  }
  const prods = ind.products.map((s) => data.products.find((p) => p.slug === s)).filter(Boolean);

  return (
    <Page name="industry">
      {/* ---- Hero ---- */}
      <section className="pd-hero ind-hero-with-bg" style={{"--ind-bg": "url(/images/industries/" + ind.slug + ".jpg)"}}>
        <div className="pd-hero-bg ind-hero-bg-img" />
        <div className="container">
          <div className="pd-back" onClick={() => navigate('/industries')}>
            <svg width="14" height="14" viewBox="0 0 16 16" style={{ marginRight: 6 }}><path d="M13 8H3M8 3L3 8L8 13" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
            All industries
          </div>
          <div className="ind-hero-inner">
            <div className="eyebrow"><span className="dot"></span>Industry Â· {ind.name}</div>
            <h1 className="pd-name" style={{ maxWidth: '18ch' }}>{ind.hero.headline}</h1>
            <p className="pd-summary" style={{ marginTop: 20, maxWidth: '64ch' }}>{ind.hero.desc}</p>
            <div className="pd-stat-row" style={{ maxWidth: 560 }}>
              <div className="pd-stat"><Stat v={ind.stat.v} l={ind.stat.l} /></div>
              <div className="pd-stat"><Stat v="< 48h" l="quote turnaround" /></div>
            </div>
            <div className="pd-hero-actions">
              <a data-link href="/quote" className="btn btn--accent btn--lg">
                Get a quote
                <span className="arrow"><svg viewBox="0 0 16 16" width="14" height="14"><path d="M4 12 L12 4 M6 4 H12 V10" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" /></svg></span>
              </a>
              <a href="tel:1300880005" className="btn btn--ghost btn--lg">Call 1300 880 005</a>
            </div>
          </div>
        </div>
      </section>

      {/* ---- Challenges ---- */}
      <section className="pd-section pd-section--alt">
        <div className="container">
          <ScrollReveal>
            <div className="eyebrow"><span className="dot"></span>What we solve</div>
            <h2 className="pd-section-title" style={{ marginBottom: 48 }}>{ind.challenges.title}</h2>
          </ScrollReveal>
          <ScrollReveal className="ind-challenges" stagger>
            {ind.challenges.items.map((c, i) => (
              <div key={i} className="ind-challenge">
                <div className="ind-challenge-num mono">0{i + 1}</div>
                <h3 className="ind-challenge-t">{c.t}</h3>
                <p className="ind-challenge-d">{c.d}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* ---- Products for this industry ---- */}
      <section className="pd-section">
        <div className="container">
          <ScrollReveal>
            <div className="eyebrow"><span className="dot"></span>The toolkit</div>
            <h2 className="pd-section-title" style={{ marginBottom: 48 }}>Products built for {ind.name.toLowerCase()}.</h2>
          </ScrollReveal>
          <ScrollReveal className="ind-products" stagger>
            {prods.map((p) => (
              <div key={p.slug} className="ind-product-card" onClick={() => navigate(`/products/${p.slug}`)}>
                <div className="ind-product-preview">
                  <ProductPreview slug={p.slug} />
                </div>
                <div className="ind-product-content">
                  <h3 className="ind-product-name">{p.name}</h3>
                  <p className="ind-product-desc">{p.summary}</p>
                  <span className="related-arrow">
                    View product
                    <svg width="14" height="14" viewBox="0 0 16 16"><path d="M3 8H13M8 3L13 8L8 13" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
                  </span>
                </div>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* ---- Capabilities ---- */}
      <section className="pd-section pd-section--alt">
        <div className="container">
          <ScrollReveal>
            <div className="pd-integ-head">
              <div>
                <div className="eyebrow"><span className="dot"></span>Capabilities</div>
                <h2 className="pd-section-title">What we bring to {ind.name.toLowerCase()}.</h2>
              </div>
            </div>
          </ScrollReveal>
          <ScrollReveal>
            <div className="pd-integ-list">
              {ind.capabilities.map((c, i) => (
                <div key={i} className="pd-integ-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="var(--accent)" opacity="0.12" /><path d="M6 10.5L8.5 13L14 7" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span>{c}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ---- Clients ---- */}
      <section className="pd-section">
        <div className="container">
          <ScrollReveal>
            <div className="eyebrow"><span className="dot"></span>Trusted by</div>
            <h2 className="pd-section-title" style={{ marginBottom: 40 }}>Organisations we work with.</h2>
          </ScrollReveal>
          <ScrollReveal className="ind-clients" stagger>
            {ind.clients.map((c) => (
              <div key={c} className="ind-client">{c}</div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* ---- Gallery ---- */}
      <section className="pd-section pd-section--alt">
        <div className="container">
          <ScrollReveal>
            <div className="eyebrow"><span className="dot"></span>In the field</div>
            <h2 className="pd-section-title" style={{ marginBottom: 12 }}>Real deployments. Real clients.</h2>
            <p className="pd-gallery-sub">Live installations from {ind.name.toLowerCase()} we work with across Australia.</p>
          </ScrollReveal>
          <ScrollReveal>
            <ProductGallery filterCaptions={ind.galleryFilter} />
          </ScrollReveal>
        </div>
      </section>

      {/* ---- CTA ---- */}
      <section className="pd-section">
        <div className="container">
          <ScrollReveal>
            <div className="pd-final-cta">
              <div className="eyebrow"><span className="dot"></span>Next step</div>
              <h2 className="h1" style={{ marginTop: 16 }}>
                Let's talk about your {ind.name.toLowerCase().replace(/s$/, '')} project.
              </h2>
              <p className="lede" style={{ marginTop: 20, maxWidth: '56ch' }}>
                Tell us what you need. We will come back with a tailored quote, concept mockups and a clear scope within 48 hours.
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 32 }}>
                <a data-link href="/quote" className="btn btn--accent btn--lg">
                  Get a quote
                  <span className="arrow"><svg viewBox="0 0 16 16" width="14" height="14"><path d="M4 12 L12 4 M6 4 H12 V10" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" /></svg></span>
                </a>
                <a href="mailto:hello@advertiseme.au" className="btn btn--ghost btn--lg">Email us</a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </Page>);
}

// ---------------- About ----------------
function AboutPage() {
  const data = AM_DATA;
  return (
    <Page name="about">
      {/* ---- Hero ---- */}
      <section className="about-hero">
        <div className="container">
          <div className="eyebrow"><span className="dot"></span>About</div>
          <h1 className="h1" style={{ marginTop: 16, maxWidth: '22ch' }}>
            A small team that builds{' '}
            <em style={{ fontFamily: "'Inter', sans-serif", fontStyle: 'italic', fontWeight: 300, color: 'var(--accent)', letterSpacing: '-0.025em' }}>big things for screens.</em>
          </h1>
          <p className="lede" style={{ marginTop: 24, maxWidth: '62ch' }}>
            We are a Sydney-based digital solutions company specialising in custom digital signage, 
            interactive touch solutions, wayfinding systems and display technology. Since 2009 we have
            designed, built and supported screen-based solutions for medical centres, hospitals, 
            shopping centres, universities and government organisations across Australia.
          </p>
        </div>
      </section>

      {/* ---- Stats ---- */}
      <section className="section section--tight" style={{ paddingTop: 0, paddingBottom: 0 }}>
        <div className="container">
          <div className="about-stats-grid">
            {data.stats.map((s) => <Stat key={s.l} v={s.v} l={s.l} />)}
          </div>
        </div>
      </section>

      {/* ---- Who we are ---- */}
      <section className="pd-section">
        <div className="container">
          <ScrollReveal>
            <div className="pr-intro-grid">
              <div>
                <div className="eyebrow"><span className="dot"></span>Who we are</div>
                <h2 className="pd-section-title">Passionate about digital technology and what it can do for your business.</h2>
              </div>
              <div>
                <p className="pr-intro-body">
                  We are a friendly, hands-on team who genuinely enjoy what we do. We spend time 
                  understanding how your organisation operates, what problems you face and how a 
                  well-designed digital solution can make a real difference. We are not afraid to 
                  explore new possibilities or build solutions that push boundaries.
                </p>
                <p className="pr-intro-body" style={{ marginTop: 16 }}>
                  Working with us is straightforward. You speak directly with the people who design, 
                  build and support your solution. No account managers relaying messages, no offshore 
                  teams you never meet. When you call, you get someone who knows your project.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ---- Our values ---- */}
      <section className="pd-section pd-section--alt">
        <div className="container">
          <ScrollReveal>
            <div className="eyebrow"><span className="dot"></span>How we work</div>
            <h2 className="pd-section-title" style={{ marginBottom: 48 }}>What you can expect from us.</h2>
          </ScrollReveal>
          <ScrollReveal className="pd-benefits" stagger>
            <div className="pd-benefit">
              <div className="pd-benefit-icon">ðŸ› ï¸</div>
              <h3 className="pd-benefit-title">Everything is custom</h3>
              <p className="pd-benefit-desc">No SaaS templates, no generic apps. Every interface is designed to your brand, your content and your environment. We write the integrations ourselves and own the solution end to end.</p>
            </div>
            <div className="pd-benefit">
              <div className="pd-benefit-icon">ðŸ¤</div>
              <h3 className="pd-benefit-title">Easy to work with</h3>
              <p className="pd-benefit-desc">We keep things simple and clear. Plain language, transparent quoting, no jargon. We explain what we recommend and why, so you always feel confident in what you are getting.</p>
            </div>
            <div className="pd-benefit">
              <div className="pd-benefit-icon">ðŸ“ž</div>
              <h3 className="pd-benefit-title">We stay after go-live</h3>
              <p className="pd-benefit-desc">Our commitment does not end at deployment. We provide ongoing support via email, phone and remote access. Content updates, troubleshooting, system monitoring. We are here for the long run.</p>
            </div>
            <div className="pd-benefit">
              <div className="pd-benefit-icon">ðŸ§ </div>
              <h3 className="pd-benefit-title">17 years of experience</h3>
              <p className="pd-benefit-desc">We have been doing this since 2009. That experience means fewer surprises, better scoping and solutions that work in the real world, not just in a demo.</p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ---- Support commitment ---- */}
      <section className="pd-section">
        <div className="container">
          <ScrollReveal>
            <div className="pr-intro-grid">
              <div>
                <div className="eyebrow"><span className="dot"></span>Post-implementation</div>
                <h2 className="pd-section-title">Support that lasts as long as your screens do.</h2>
              </div>
              <div>
                <p className="pr-intro-body">
                  A screen that goes offline costs you credibility. That is why every deployment 
                  is monitored from our Sydney office with a 4-hour support response window. 
                  We handle content updates, system diagnostics and configuration changes remotely, 
                  so most issues are resolved without anyone stepping foot on your site.
                </p>
                <p className="pr-intro-body" style={{ marginTop: 16 }}>
                  Our support is not a bolt-on. It is built into how we work. Whether you need 
                  a quick content change or a system-wide update across multiple locations, our 
                  team is a phone call or email away. No lock-in contracts, no ticket queues, 
                  no waiting days for a response.
                </p>
              </div>
            </div>
          </ScrollReveal>
          <ScrollReveal className="pd-support-grid" stagger style={{ marginTop: 48 }}>
            <div className="pd-support-card">
              <div className="pd-support-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7L12 13L2 7" /></svg>
              </div>
              <h3 className="pd-support-title">Email support</h3>
              <p className="pd-support-desc">Send a request any time. We respond within 4 hours during business hours, often sooner.</p>
              <a href="mailto:hello@advertiseme.au" className="pd-support-link">hello@advertiseme.au</a>
            </div>
            <div className="pd-support-card">
              <div className="pd-support-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
              </div>
              <h3 className="pd-support-title">Phone support</h3>
              <p className="pd-support-desc">Call our Sydney team directly. If we miss you, we call back the same day.</p>
              <a href="tel:1300880005" className="pd-support-link">1300 880 005</a>
            </div>
            <div className="pd-support-card">
              <div className="pd-support-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
              </div>
              <h3 className="pd-support-title">Remote support</h3>
              <p className="pd-support-desc">We connect to your screens remotely to diagnose, update and resolve issues without an on-site visit.</p>
              <span className="pd-support-link">Included with every deployment</span>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ---- Accreditations + NSW Gov ---- */}
      <section className="pd-section pd-section--alt">
        <div className="container">
          <ScrollReveal>
            <div className="pr-intro-grid">
              <div>
                <div className="eyebrow"><span className="dot"></span>Accreditations</div>
                <h2 className="pd-section-title">Trusted by government. Proven in the field.</h2>
              </div>
              <div>
                <p className="pr-intro-body">
                  We hold the standards that matter. Our team has achieved recognised industry 
                  accreditations and we maintain them because our clients, particularly in 
                  healthcare and government, expect nothing less.
                </p>
              </div>
            </div>
          </ScrollReveal>
          <ScrollReveal style={{ marginTop: 48 }}>
            <div className="abt-gov-block">
              <div className="abt-gov-badge">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M12 2L3 7v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>
              <div className="abt-gov-content">
                <h3 className="abt-gov-title">NSW Government ICT Services Scheme</h3>
                <h4 className="abt-gov-subtitle">Registered Supplier</h4>
                <p className="abt-gov-desc">
                  We are an approved supplier on the NSW Government ICT Services Scheme, enabling 
                  government departments and agencies to engage us directly for digital signage and 
                  interactive display projects. We have delivered solutions for a number of NSW 
                  Government organisations including NSW Health hospitals and facilities.
                </p>
                <div className="abt-gov-clients">
                  <span className="abt-gov-client">NSW Police</span>
                  <span className="abt-gov-client">Liverpool Hospital</span>
                  <span className="abt-gov-client">Bankstown Hospital</span>
                  <span className="abt-gov-client">Campbelltown Hospital</span>
                  <span className="abt-gov-client">Canterbury Bankstown Council</span>
                  <span className="abt-gov-client">Cancer Institute</span>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ---- Clients ---- */}
      <section className="pd-section">
        <div className="container">
          <ScrollReveal>
            <div className="eyebrow"><span className="dot"></span>Our clients</div>
            <h2 className="pd-section-title" style={{ marginBottom: 40 }}>Trusted by organisations across Australia.</h2>
          </ScrollReveal>
          <ScrollReveal className="ind-clients" stagger>
            {data.clients.map((c) => (
              <div key={c} className="ind-client">{c}</div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* ---- CTA ---- */}
      <section className="pd-section pd-section--alt">
        <div className="container">
          <ScrollReveal>
            <div className="pd-final-cta">
              <div className="eyebrow"><span className="dot"></span>Let's talk</div>
              <h2 className="h1" style={{ marginTop: 16 }}>
                Ready to work with a team that<br />
                actually cares about your screens?
              </h2>
              <p className="lede" style={{ marginTop: 20, maxWidth: '56ch' }}>
                Tell us about your project. We will come back with a tailored quote, concept mockups 
                and a clear scope within 48 hours.
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 32 }}>
                <a data-link href="/quote" className="btn btn--accent btn--lg">
                  Get a quote
                  <span className="arrow"><svg viewBox="0 0 16 16" width="14" height="14"><path d="M4 12 L12 4 M6 4 H12 V10" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" /></svg></span>
                </a>
                <a href="tel:1300880005" className="btn btn--ghost btn--lg">Call 1300 880 005</a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </Page>);

}

// ---------------- Quote ----------------
const FORMSPREE_ID = 'mdavydpg'; // â† replace with your Formspree form ID

function QuotePage() {
  const data = AM_DATA;
  const [sel, setSel] = useStateP([]);
  const [otherText, setOtherText] = useStateP("");
  const [sent, setSent] = useStateP(false);
  const [sending, setSending] = useStateP(false);
  const [formError, setFormError] = useStateP(null);
  const formRef = useRefP(null);
  const toggle = (slug) => setSel((prev) => prev.includes(slug) ? prev.filter((x) => x !== slug) : [...prev, slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setFormError(null);
    const fd = new FormData(formRef.current);
    const selectedProducts = sel.map(s => {
      if (s === 'other') return otherText ? `Other: ${otherText}` : 'Other';
      return data.products.find(p => p.slug === s)?.name;
    }).filter(Boolean).join(', ');
    try {
      const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          name: fd.get('name'),
          company: fd.get('company'),
          email: fd.get('email'),
          phone: fd.get('phone'),
          industry: fd.get('industry'),
          project: fd.get('project'),
          products: selectedProducts || 'Not specified',
        }),
      });
      if (res.ok) {
        setSent(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setFormError('Something went wrong. Please email us directly at hello@advertiseme.au');
      }
    } catch {
      setFormError('Something went wrong. Please email us directly at hello@advertiseme.au');
    }
    setSending(false);
  };

  return (
    <Page name="quote">
      <section className="container quote-hero">
        <div>
          <div className="eyebrow"><span className="dot"></span>Get a free quote</div>
          <h1 className="h1" style={{ marginTop: 16 }}>
            Tell us about<br />your screens.
          </h1>
          <p className="lede" style={{ marginTop: 24 }}>
            We keep it simple â€” a real response, from a real person, within 48 hours.
          </p>

          {sent ?
          <div className="quote-success" style={{ marginTop: 32 }}>
              <h3>Got it.</h3>
              <p className="body">We've received your brief. Expect a tailored quote and
                concept mockups in your inbox within 48 hours. In the meantime, feel free
                to keep exploring.</p>
              <div style={{ marginTop: 20 }}>
                <a data-link href="/" className="btn btn--ghost">â† Back to home</a>
              </div>
            </div> :

          <form className="quote-form" style={{ marginTop: 32 }} onSubmit={submit} ref={formRef}>
              <div className="field">
                <label>What are you interested in?</label>
                <div className="quote-product-pick">
                  {data.products.map((p) =>
                <div key={p.slug}
                className={`quote-product-opt ${sel.includes(p.slug) ? 'active' : ''}`}
                onClick={() => toggle(p.slug)}>
                      <span className="quote-product-opt-name">{p.name}</span>
                      <span className="quote-product-opt-tag">{p.tagline}</span>
                      <span className="quote-product-opt-check">âœ“</span>
                    </div>
                )}
                  <div
                    className={`quote-product-opt ${sel.includes('other') ? 'active' : ''}`}
                    onClick={() => toggle('other')}>
                    <span className="quote-product-opt-name">Other</span>
                    <span className="quote-product-opt-tag">Something else â€” tell us below</span>
                    <span className="quote-product-opt-check">âœ“</span>
                  </div>
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label>Your name</label>
                  <input type="text" name="name" required placeholder="e.g. Jane Doe" />
                </div>
                <div className="field">
                  <label>Company / organisation</label>
                  <input type="text" name="company" required placeholder="e.g. Northside Medical" />
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label>Email</label>
                  <input type="email" name="email" required placeholder="you@company.com.au" />
                </div>
                <div className="field">
                  <label>Phone</label>
                  <input type="tel" name="phone" required placeholder="+61 4xx xxx xxx" />
                </div>
              </div>

              <div className="field">
                <label>Industry</label>
                <select name="industry" defaultValue="">
                  <option value="" disabled>Selectâ€¦</option>
                  <option>Medical centre / clinic</option>
                  <option>Hospital</option>
                  <option>Shopping centre / retail</option>
                  <option>University / research</option>
                  <option>Aged care</option>
                  <option>Council / public</option>
                  <option>Other</option>
                </select>
              </div>

              <div className="field">
                <label>Tell us about your project</label>
                <textarea name="project" required placeholder="Where will the screens go? What systems do they need to connect to?" />
              </div>

              {formError && <p className="quote-error">{formError}</p>}

              <button type="submit" className="btn btn--accent btn--lg" style={{ marginTop: 8 }} disabled={sending}>
                {sending ? 'Sendingâ€¦' : 'Send brief'}
                {!sending && <span className="arrow"><svg viewBox="0 0 16 16" width="14" height="14"><path d="M4 12 L12 4 M6 4 H12 V10" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" /></svg></span>}
              </button>
            </form>
          }
        </div>

        <aside className="quote-side">
          <div className="eyebrow"><span className="dot"></span>What happens next</div>
          <ul>
            <li><span className="num mono">01</span>We read your brief and discuss it internally with our team.</li>
            <li><span className="num mono">02</span>We reach out by email or phone to talk through your project and confirm your requirements.</li>
            <li><span className="num mono">03</span>You get the chance to ask us anything â€” no question is too small.</li>
            <li><span className="num mono">04</span>We put together a tailored quote along with concept wireframes or mockups.</li>
            <li><span className="num mono">05</span>We follow up to answer any final questions and see if you'd like to proceed.</li>
            <li><span className="num mono">06</span>If you're happy to go ahead, we request a deposit and kick off the project.</li>
          </ul>
          <div style={{ marginTop: 32, padding: 24, border: '1px solid var(--line)', borderRadius: 6 }}>
            <div className="eyebrow"><span className="dot"></span>Prefer to email?</div>
            <div className="h3" style={{ marginTop: 12 }}>
              <a href="mailto:hello@advertiseme.au" style={{ color: 'var(--accent)' }}>hello@advertiseme.au</a>
            </div>
            <div className="body" style={{ marginTop: 4 }}>We reply within 48 hours, usually sooner.</div>
          </div>
          <div style={{ marginTop: 12, padding: 24, border: '1px solid var(--line)', borderRadius: 6 }}>
            <div className="eyebrow"><span className="dot"></span>Or give us a call</div>
            <div className="h3" style={{ marginTop: 12 }}>1300 880 005</div>
            <div className="body" style={{ marginTop: 4 }}>Sydney HQ Â· Monâ€“Fri 8:30â€“17:30 AEST</div>
            <div className="body" style={{ marginTop: 8, color: 'var(--muted-2)', fontSize: 13 }}>We're a small team â€” if we miss you, leave a message and we'll call back.</div>
          </div>
        </aside>
      </section>
    </Page>);

}

// ================================================================
// Pricing page
// ================================================================
function PricingPage({ navigate }) {
  const data = AM_DATA;

  return (
    <Page name="pricing">
      {/* ---- Hero ---- */}
      <section className="about-hero">
        <div className="container">
          <div className="eyebrow"><span className="dot"></span>Pricing</div>
          <h1 className="h1" style={{ marginTop: 16, maxWidth: '22ch' }}>
            Transparent pricing,{' '}
            <em style={{ fontFamily: "'Inter', sans-serif", fontStyle: 'italic', fontWeight: 300, color: 'var(--accent)', letterSpacing: '-0.025em' }}>
              tailored to your project.
            </em>
          </h1>
          <p className="lede" style={{ marginTop: 24, maxWidth: '62ch' }}>
            We do not publish fixed prices because no two projects are the same. Instead, we scope every 
            engagement around your specific requirements and provide a transparent, itemised quote you can 
            review line by line. Here is how it works.
          </p>
        </div>
      </section>

      {/* ---- How we price ---- */}
      <section className="pd-section">
        <div className="container">
          <ScrollReveal>
            <div className="pr-intro-grid">
              <div>
                <div className="eyebrow"><span className="dot"></span>Our approach</div>
                <h2 className="pd-section-title">Every quote is scoped to your requirements.</h2>
              </div>
              <div>
                <p className="pr-intro-body">
                  Whether you need a single screen in a GP clinic or a fleet of kiosks across a hospital campus, 
                  the pricing is built around what you actually need. We walk you through every line item so there 
                  are no surprises, no hidden fees and no unnecessary extras. The goal is a solution that fits your 
                  budget and solves your problem.
                </p>
                <p className="pr-intro-body" style={{ marginTop: 16 }}>
                  Every project is generally made up of three components. The mix varies depending on complexity, 
                  but you will always see each one broken out clearly in your quote.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ---- Three pillars ---- */}
      <section className="pd-section pd-section--alt">
        <div className="container">

          {/* HARDWARE */}
          <ScrollReveal className="pr-pillar">
            <div className="pr-pillar-header">
              <div className="pr-pillar-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" />
                  <path d="M8 21h8M12 17v4" />
                </svg>
              </div>
              <div className="pr-pillar-num mono">01</div>
            </div>
            <h3 className="pr-pillar-title">Hardware</h3>
            <p className="pr-pillar-subtitle">The physical screens, stands and peripherals that make up your deployment.</p>
            <p className="pr-pillar-body">
              Hardware costs depend on what you need and how many you need. A single wall-mounted screen for 
              a medical centre lobby is a very different scope to ten floor-standing kiosks across a shopping centre. 
              We source commercial-grade displays and enclosures from trusted suppliers and include everything 
              required for your environment.
            </p>
            <div className="pr-pillar-factors">
              <div className="pr-pillar-factor">
                <span className="pr-pillar-factor-label">Number of units</span>
                <span className="pr-pillar-factor-desc">How many screens or kiosks you need across your site or sites.</span>
              </div>
              <div className="pr-pillar-factor">
                <span className="pr-pillar-factor-label">Screen type and size</span>
                <span className="pr-pillar-factor-desc">Standard displays, touchscreens, high-brightness outdoor panels, or anti-glare medical-grade options.</span>
              </div>
              <div className="pr-pillar-factor">
                <span className="pr-pillar-factor-label">Mounting and enclosures</span>
                <span className="pr-pillar-factor-desc">Wall mounts, floor stands, countertop brackets, custom kiosk enclosures, or trolley units.</span>
              </div>
              <div className="pr-pillar-factor">
                <span className="pr-pillar-factor-label">Peripherals</span>
                <span className="pr-pillar-factor-desc">Media players, cabling, network adapters, protective cases, or any additional components your setup requires.</span>
              </div>
            </div>
          </ScrollReveal>

          {/* SOFTWARE */}
          <ScrollReveal className="pr-pillar">
            <div className="pr-pillar-header">
              <div className="pr-pillar-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M9 9l3 3-3 3M15 15h-3" />
                </svg>
              </div>
              <div className="pr-pillar-num mono">02</div>
            </div>
            <h3 className="pr-pillar-title">Software</h3>
            <p className="pr-pillar-subtitle">The cloud platform, modules and ongoing services that power your screens.</p>
            <p className="pr-pillar-body">
              Software pricing is based on the number of screens you operate and the functionality you need. 
              A basic content slideshow uses less platform resource than a live bed management dashboard 
              pulling data from hospital systems. We offer flexible licensing so you only pay for what you use, 
              and you can add modules as your needs grow.
            </p>
            <div className="pr-pillar-factors">
              <div className="pr-pillar-factor">
                <span className="pr-pillar-factor-label">Number of screens</span>
                <span className="pr-pillar-factor-desc">Each active screen is licensed on our cloud platform. Volume pricing applies for larger deployments.</span>
              </div>
              <div className="pr-pillar-factor">
                <span className="pr-pillar-factor-label">Product type</span>
                <span className="pr-pillar-factor-desc">A simple poster slideshow is priced differently to an interactive kiosk with wayfinding and live data feeds.</span>
              </div>
              <div className="pr-pillar-factor">
                <span className="pr-pillar-factor-label">Modules and add-ons</span>
                <span className="pr-pillar-factor-desc">Roster sync, queue management, analytics, scheduling, feedback forms, and other modules are priced individually.</span>
              </div>
              <div className="pr-pillar-factor">
                <span className="pr-pillar-factor-label">API and third-party services</span>
                <span className="pr-pillar-factor-desc">If your project requires integration with external APIs or third-party data services, any associated costs are included in the quote.</span>
              </div>
            </div>
          </ScrollReveal>

          {/* PROFESSIONAL SERVICES */}
          <ScrollReveal className="pr-pillar">
            <div className="pr-pillar-header">
              <div className="pr-pillar-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              <div className="pr-pillar-num mono">03</div>
            </div>
            <h3 className="pr-pillar-title">Professional Services</h3>
            <p className="pr-pillar-subtitle">The design, build, integration and support work that brings your project to life.</p>
            <p className="pr-pillar-body">
              This is where the custom work happens. Professional services cover everything from the initial 
              interface design through to system integrations, testing and handover. The scope depends on 
              how complex your project is. A standard deployment with a pre-built template takes less time 
              than a fully bespoke interface with custom integrations. Either way, you see exactly what is 
              included before you commit.
            </p>
            <div className="pr-pillar-factors">
              <div className="pr-pillar-factor">
                <span className="pr-pillar-factor-label">Interface design</span>
                <span className="pr-pillar-factor-desc">Custom UI design matched to your brand, including wireframes, mockups and revisions until you are happy.</span>
              </div>
              <div className="pr-pillar-factor">
                <span className="pr-pillar-factor-label">Template and module build</span>
                <span className="pr-pillar-factor-desc">Building your content templates, configuring modules, and setting up your content management dashboard.</span>
              </div>
              <div className="pr-pillar-factor">
                <span className="pr-pillar-factor-label">System integrations</span>
                <span className="pr-pillar-factor-desc">Connecting to your internal systems (practice management, hospital ADT, building management) or external APIs.</span>
              </div>
              <div className="pr-pillar-factor">
                <span className="pr-pillar-factor-label">Delivery and configuration</span>
                <span className="pr-pillar-factor-desc">Pre-configuring hardware and software, preparing deployment instructions and providing remote setup support.</span>
              </div>
              <div className="pr-pillar-factor">
                <span className="pr-pillar-factor-label">Training and documentation</span>
                <span className="pr-pillar-factor-desc">User guides, admin training sessions and ongoing reference documentation for your team.</span>
              </div>
            </div>
          </ScrollReveal>

        </div>
      </section>

      {/* ---- Process ---- */}
      <section className="pd-section">
        <div className="container">
          <ScrollReveal>
            <div className="eyebrow"><span className="dot"></span>The process</div>
            <h2 className="pd-section-title" style={{ marginBottom: 48 }}>From enquiry to itemised quote in 48 hours.</h2>
          </ScrollReveal>
          <ScrollReveal className="pr-process" stagger>
            <div className="pr-process-step">
              <div className="pr-process-num mono">01</div>
              <h3 className="pr-process-title">You tell us what you need</h3>
              <p className="pr-process-desc">
                Fill in our quote form or send us an email. Include as much or as little detail as you have. 
                We will ask the right questions to fill in any gaps.
              </p>
            </div>
            <div className="pr-process-step">
              <div className="pr-process-num mono">02</div>
              <h3 className="pr-process-title">We scope your project</h3>
              <p className="pr-process-desc">
                We review your requirements, assess the hardware and software needed, and determine 
                what professional services are involved. If anything is unclear, we reach out to confirm.
              </p>
            </div>
            <div className="pr-process-step">
              <div className="pr-process-num mono">03</div>
              <h3 className="pr-process-title">You receive a clear, itemised quote</h3>
              <p className="pr-process-desc">
                Within 48 hours you receive a quote that breaks down hardware, software and professional 
                services line by line. No vague totals, no bundled mystery costs. We include concept wireframes 
                so you can see what you are getting.
              </p>
            </div>
            <div className="pr-process-step">
              <div className="pr-process-num mono">04</div>
              <h3 className="pr-process-title">We refine until it fits</h3>
              <p className="pr-process-desc">
                Have questions? Need to adjust scope or budget? We work with you to refine the quote until 
                it matches your requirements and expectations. No pressure, no lock-in.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ---- What you won't find ---- */}
      <section className="pd-section pd-section--alt">
        <div className="container">
          <ScrollReveal>
            <div className="pr-promise-grid">
              <div>
                <div className="eyebrow"><span className="dot"></span>Our commitment</div>
                <h2 className="pd-section-title">What you will always get from us.</h2>
              </div>
              <div className="pr-promise-list">
                <div className="pr-promise-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="var(--accent)" opacity="0.12" /><path d="M6 10.5L8.5 13L14 7" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span>A fully itemised quote with no hidden fees</span>
                </div>
                <div className="pr-promise-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="var(--accent)" opacity="0.12" /><path d="M6 10.5L8.5 13L14 7" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span>Concept wireframes or mockups included before you commit</span>
                </div>
                <div className="pr-promise-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="var(--accent)" opacity="0.12" /><path d="M6 10.5L8.5 13L14 7" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span>No lock-in contracts unless your solution requires third-party infrastructure or services</span>
                </div>
                <div className="pr-promise-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="var(--accent)" opacity="0.12" /><path d="M6 10.5L8.5 13L14 7" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span>A 48 hour turnaround from enquiry to quote</span>
                </div>
                <div className="pr-promise-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="var(--accent)" opacity="0.12" /><path d="M6 10.5L8.5 13L14 7" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span>Direct communication with the people building your solution</span>
                </div>
                <div className="pr-promise-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="var(--accent)" opacity="0.12" /><path d="M6 10.5L8.5 13L14 7" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span>Flexible payment terms for larger projects</span>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ---- CTA ---- */}
      <section className="pd-section">
        <div className="container">
          <ScrollReveal>
            <div className="pd-final-cta">
              <div className="eyebrow"><span className="dot"></span>Ready to start?</div>
              <h2 className="h1" style={{ marginTop: 16 }}>
                Tell us about your project.<br />
                We will handle the rest.
              </h2>
              <p className="lede" style={{ marginTop: 20, maxWidth: '56ch' }}>
                Send us a brief and we will come back with a tailored, itemised quote and concept mockups within 48 hours. No obligation, no pressure.
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 32 }}>
                <a data-link href="/quote" className="btn btn--accent btn--lg">
                  Get a free quote
                  <span className="arrow"><svg viewBox="0 0 16 16" width="14" height="14"><path d="M4 12 L12 4 M6 4 H12 V10" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" /></svg></span>
                </a>
                <a href="tel:1300880005" className="btn btn--ghost btn--lg">Call 1300 880 005</a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ---- Products overview ---- */}
      <section className="pd-section pd-section--alt" style={{ paddingBottom: 0 }}>
        <div className="container">
          <div className="eyebrow"><span className="dot"></span>Our products</div>
          <h2 className="pd-section-title" style={{ marginBottom: 32 }}>Explore what we build.</h2>
        </div>
        <div className="container" style={{ padding: 0, maxWidth: 'var(--max)' }}>
          <div className="related">
            {data.products.slice(0, 3).map((r) =>
            <div key={r.slug} className="related-card" onClick={() => navigate(`/products/${r.slug}`)}>
                <div className="related-num mono">0{data.products.findIndex((x) => x.slug === r.slug) + 1}</div>
                <div className="related-name">{r.name}</div>
                <div className="related-summary">{r.summary}</div>
                <span className="related-arrow">
                  View product
                  <svg width="14" height="14" viewBox="0 0 16 16"><path d="M3 8H13M8 3L13 8L8 13" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
                </span>
              </div>
            )}
          </div>
        </div>
      </section>
    </Page>);
}



/* â”€â”€â”€ Portfolio Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
function PortfolioPage() {
  const modules = import.meta.glob('/public/images/portfolio/processed/*.{jpg,jpeg,png,webp}', {
    eager: true, query: '?url', import: 'default'
  });

  const allItems = Object.entries(modules).map(([p, url]) => {
    const file = p.split('/').pop().replace(/\.[^.]+$/, '');
    const orientation = file.endsWith('-portrait') || file.includes('-portrait-') ? 'portrait' : 'landscape';
    const caption = file
      .replace(/-portrait(-\d+)?$/, '')
      .replace(/-landscape(-\d+)?$/, '')
      .replace(/-/g, ' ');

    // Categorise based on filename keywords â€” images can belong to multiple categories
    const categories = [];
    const f = file.toLowerCase();
    if (f.includes('hospital') || f.includes('nsw-health') || f.includes('liverpool') || f.includes('bed-management'))
      categories.push('hospitals');
    if (f.includes('medical-centre') || f.includes('medical-practice') || f.includes('medical-reception') || f.includes('dermatology') || f.includes('child-vaccination') || f.includes('medical-waiting'))
      categories.push('medical-centres');
    if (f.includes('university') || f.includes('gotafe') || f.includes('unsw') || f.includes('student') || f.includes('researcher') || f.includes('clinical-skills') || f.includes('campus'))
      categories.push('education');
    if (f.includes('wayfinding') || f.includes('directory'))
      categories.push('wayfinding');
    if (f.includes('eposter') || f.includes('e-poster'))
      categories.push('eposters');
    if (f.includes('conference') || f.includes('event') || f.includes('isprm') || f.includes('exhibition'))
      categories.push('conferences');
    // eposters at conferences also show in conferences
    if (categories.includes('eposters') && !categories.includes('conferences'))
      if (f.includes('conference') || f.includes('health-research') || f.includes('presenting'))
        categories.push('conferences');
    // hospital wayfinding/directory also shows in wayfinding
    if (categories.includes('hospitals') && !categories.includes('wayfinding'))
      if (f.includes('wayfinding') || f.includes('directory'))
        categories.push('wayfinding');
    // anything not yet categorised goes into digital-signage
    if (categories.length === 0) categories.push('digital-signage');

    return { img: url, caption, orientation, categories, file };
  }).sort((a, b) => a.caption.localeCompare(b.caption));

  const categories = [
    { key: 'all', label: 'All' },
    { key: 'hospitals', label: 'Hospitals' },
    { key: 'medical-centres', label: 'Medical Centres' },
    { key: 'education', label: 'Education & Research' },
    { key: 'wayfinding', label: 'Wayfinding & Directories' },
    { key: 'eposters', label: 'ePoster Displays' },
    { key: 'conferences', label: 'Conferences & Events' },
    { key: 'digital-signage', label: 'Digital Signage' },
  ];

  const [activeFilter, setActiveFilter] = useStateP('all');
  const [lightbox, setLightbox] = useStateP(null);
  const [animating, setAnimating] = useStateP(false);
  const gridRef = useRefP(null);
  const pillRefs = useRefP({});
  const indicatorRef = useRefP(null);

  const filtered = activeFilter === 'all' ? allItems : allItems.filter(i => i.categories.includes(activeFilter));

  // Animate filter indicator pill
  useEffectP(() => {
    const pill = pillRefs.current[activeFilter];
    const indicator = indicatorRef.current;
    if (pill && indicator) {
      const bar = pill.parentElement;
      const barRect = bar.getBoundingClientRect();
      const pillRect = pill.getBoundingClientRect();
      indicator.style.width = pillRect.width + 'px';
      indicator.style.left = (pillRect.left - barRect.left) + 'px';
    }
  }, [activeFilter]);

  // Filter change with stagger animation
  const handleFilter = (key) => {
    if (key === activeFilter) return;
    setAnimating(true);
    setTimeout(() => {
      setActiveFilter(key);
      setTimeout(() => setAnimating(false), 50);
    }, 250);
  };

  // 3D tilt on hover
  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const tiltX = (0.5 - y) * 12;
    const tiltY = (x - 0.5) * 12;
    const glowX = x * 100;
    const glowY = y * 100;
    card.style.transform = 'perspective(800px) rotateX(' + tiltX + 'deg) rotateY(' + tiltY + 'deg) scale3d(1.03,1.03,1.03)';
    card.querySelector('.pf-card-glow').style.background =
      'radial-gradient(circle at ' + glowX + '% ' + glowY + '%, rgba(100,180,255,0.15) 0%, transparent 60%)';
  };

  const handleMouseLeave = (e) => {
    const card = e.currentTarget;
    card.style.transform = '';
    card.querySelector('.pf-card-glow').style.background = 'transparent';
  };

  // Lightbox ESC handler
  useEffectP(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <section className="pf-page">
      <div className="pf-hero">
        <h1 className="pf-hero-title">Our Work</h1>
        <p className="pf-hero-sub">Digital solutions deployed across hospitals, universities, medical centres and public spaces throughout Australia.</p>

      </div>

      <div className="pf-filter-bar" role="tablist">
        <div className="pf-filter-track">
          <div className="pf-filter-indicator" ref={indicatorRef}></div>
          {categories.map(cat => (
            <button
              key={cat.key}
              ref={el => pillRefs.current[cat.key] = el}
              className={'pf-filter-pill' + (activeFilter === cat.key ? ' active' : '')}
              onClick={() => handleFilter(cat.key)}
              role="tab"
              aria-selected={activeFilter === cat.key}
            >
              {cat.label}
              {activeFilter === cat.key && (
                <span className="pf-filter-count">{filtered.length}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className={'pf-grid' + (animating ? ' pf-grid-exit' : '')} ref={gridRef}>
        {filtered.map((item, idx) => (
          <div
            key={item.file}
            className={'pf-card ' + item.orientation}
            style={{ animationDelay: (idx * 40) + 'ms' }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={() => setLightbox(item)}
          >
            <div className="pf-card-glow"></div>
            <img
              src={item.img}
              alt={item.caption}
              className="pf-card-img"
              loading="lazy"
              draggable="false"
            />
            <div className="pf-card-overlay">
              <span className="pf-card-caption">{item.caption}</span>
            </div>
            <div className="pf-card-edge"></div>
          </div>
        ))}
      </div>

      {lightbox && (
        <div className="pf-lightbox" onClick={() => setLightbox(null)}>
          <div className="pf-lightbox-inner" onClick={e => e.stopPropagation()}>
            <img src={lightbox.img} alt={lightbox.caption} className="pf-lightbox-img" />
            <div className="pf-lightbox-caption">{lightbox.caption}</div>
            <button className="pf-lightbox-close" onClick={() => setLightbox(null)} aria-label="Close">Ã—</button>
          </div>
        </div>
      )}
    </section>
  );
}



// ============================================================
// Demos Page — interactive demo showcase with info panels
// ============================================================

const DEMOS = [
  {
    id: 'interactive-timetable-board',
    name: 'Timetable Board',
    category: 'Education',
    orientation: 'portrait',
    src: '/demo/interactive/interactive-timetable-board.html',
    headline: 'Interactive Timetable Board',
    description: 'Real-time class schedules for university and school buildings. Sessions cycle through the day with live status updates, expandable detail panels and venue maps.',
    features: [
      { icon: 'icon-clock', title: 'Simulated Live Clock', desc: 'Accelerated clock cycles through the day showing "On Now" and "Coming Up" sessions with countdown timers.' },
      { icon: 'icon-tap', title: 'Tap to Expand', desc: 'Tap any class to reveal full details including description, teacher, room capacity and a schematic floor plan.' },
      { icon: 'icon-pin', title: 'Venue Floor Plan', desc: 'Expanded view highlights the exact room location and indicates which lift core to use.' },
      { icon: 'icon-cycle', title: 'Auto-Cycling Sessions', desc: 'Classes automatically transition out when finished with smooth exit animations, keeping the board current.' },
      { icon: 'icon-announce', title: 'Event Banners', desc: 'Rotating footer banners promote campus events, deadlines and announcements with dot-pagination.' },
      { icon: 'icon-palette', title: 'Configurable Tweaks', desc: 'Accent colours, row density, row style and time speed are all adjustable via the built-in tweaks panel.' },
    ],
  },
  {
    id: 'interactive-expo-schedule-board',
    name: 'Expo Schedule',
    category: 'Events',
    orientation: 'portrait',
    src: '/demo/interactive/interactive-expo-schedule-board.html',
    headline: 'Interactive Expo Schedule Board',
    description: 'A health summit schedule board with keynotes, speakers, product demos and showcases. Sponsor banners, per-session QR codes and a downloadable app link.',
    features: [
      { icon: 'icon-mic', title: 'Session Types', desc: 'Colour-coded chips distinguish Keynotes, Speakers, Product Demos, Showcases, and Opening/Closing ceremonies at a glance.' },
      { icon: 'icon-phone', title: 'QR Codes', desc: 'Every session has a unique QR code in its expanded view linking to full session details, speaker bio and resources.' },
      { icon: 'icon-diamond', title: 'Sponsor Banners', desc: 'Rotating footer banners showcase Diamond and Gold sponsors with branded gradients and QR links to their websites.' },
      { icon: 'icon-pin', title: 'Venue Map', desc: 'Schematic venue layout highlights the target hall across Ground, Level 1 and Level 2 with a "You are going here" marker.' },
      { icon: 'icon-download', title: 'App Download', desc: 'Header QR code links to the event app for attendees to browse the full schedule on their phone.' },
      { icon: 'icon-tag', title: 'Topic Tags', desc: 'Sessions are tagged by topic (AI & Technology, Microbiome, Mental Health) for quick thematic scanning.' },
    ],
  },
  {
    id: 'interactive-directory-board',
    name: 'Directory Board',
    category: 'Corporate',
    orientation: 'portrait',
    src: '/demo/interactive/interactive-directory-board.html',
    headline: 'Interactive Corporate Directory Board',
    description: 'A multi-level building tenant directory with live open/closed status, tap-to-expand detail modals, a full inline editor and automatic light/dark theme switching.',
    features: [
      { icon: 'icon-building', title: 'Multi-Level Layout', desc: 'Tenants displayed across 8 levels with proportional row heights. The ground floor is gold-highlighted as the "You Are Here" location.' },
      { icon: 'icon-dot', title: 'Live Open/Closed Status', desc: 'Each tenant shows real-time open or closed status based on their configured hours, with a subtle red dot for closed tenants.' },
      { icon: 'icon-reno', title: 'Renovation Status', desc: 'Tenants under renovation display a striped amber banner and a distinct "Renovations" badge.' },
      { icon: 'icon-bulb', title: 'Light & Dark Themes', desc: 'Auto-switches to dark mode at 5pm and light at 6am. Manual toggle available in the header.' },
      { icon: 'icon-edit', title: 'Inline Editor', desc: 'Long-press the logo for 5 seconds to enter Editor Mode. Edit tenants, manage banners, customise the header, add/remove levels and tenants.' },
      { icon: 'icon-palette', title: 'Theme Editor', desc: 'Full Theme Editor with header customisation (font, colours, logo upload), directory management with row capacity meter, and banner controls.' },
      { icon: 'icon-save', title: 'Persistent Changes', desc: 'All editor changes auto-save to localStorage and persist across browser sessions. Reset to defaults available in the editor bar.' },
      { icon: 'icon-announce', title: 'Building Announcements', desc: 'Rotating footer banners for maintenance notices, new tenant welcomes, parking updates and sustainability achievements.' },
    ],
  },
  {
    id: 'interactive-eposter-board',
    name: 'ePoster Gallery',
    category: 'Research',
    orientation: 'portrait',
    src: '/demo/interactive/interactive-eposter-board.html',
    headline: 'Interactive ePoster Gallery',
    description: 'A digital poster gallery for conferences and research events. Browse 50 posters in 3x3, 4x4 or 5x5 grid layouts, swipe between pages, then tap any poster to zoom, pan and explore the full detail.',
    features: [
      { icon: 'icon-grid', title: 'Flexible Grid Layouts', desc: '3x3, 4x4 and 5x5 grid modes display 9, 16 or 25 posters per page. Switch instantly from the header without losing your place.' },
      { icon: 'icon-swipe', title: 'Swipe Between Pages', desc: 'Drag left or right to flip between poster pages, or use the arrow buttons and dot pagination in the footer.' },
      { icon: 'icon-zoom', title: 'Pinch and Zoom', desc: 'Double-tap any poster to zoom to 2.5x. Scroll to zoom up to 5x. Click and drag to pan around and focus on specific content.' },
      { icon: 'icon-poster', title: 'Full-Screen Viewer', desc: 'Posters fill the entire canvas when opened. A compact bottom bar with Exit, Prev, Next and More Info keeps controls out of the way.' },
      { icon: 'icon-info', title: 'Poster Details', desc: 'More Info opens a modal with the full abstract, author details, institution, keywords, view count and year of presentation.' },
      { icon: 'icon-star', title: 'Rate Posters', desc: 'Attendees can give each poster a 1 to 5 star rating directly from the viewer. Ratings persist during the session.' },
    ],
  },
  {
    id: 'interactive-expo-directory-board',
    name: 'Expo Directory Board',
    category: 'Events',
    orientation: 'portrait',
    src: '/demo/interactive/interactive-expo-directory-board.html',
    headline: 'Interactive Expo Directory Board',
    description: 'A touchscreen exhibitor directory for trade shows and expos. Tap any exhibitor to see contact details, a QR code and a live wayfinding path drawn across the show floor, or filter Platinum, Gold, Silver and Bronze exhibitors by A–Z and category.',
    features: [
      { icon: 'icon-pin', title: 'Live Wayfinding', desc: 'Tap any exhibitor to draw an animated path from the "You Are Here" marker straight to their booth on the expo floor plan.' },
      { icon: 'icon-tag', title: 'Tiered Directory', desc: 'Platinum, Gold, Silver and Bronze exhibitors are grouped into sponsor tiers with A–Z tabs and category filtering.' },
      { icon: 'icon-phone', title: 'QR Codes', desc: 'Every exhibitor\'s expanded panel includes a scannable QR code linking through to their contact details and website.' },
      { icon: 'icon-diamond', title: 'Sponsor Carousel', desc: 'Rotating footer banners showcase the platinum sponsor and keynote session alongside a static "Powered By" panel.' },
      { icon: 'icon-tap', title: 'Attract Mode', desc: 'After 45 seconds of inactivity the board resets and shows a pulsing "Touch to explore" prompt to draw in passers-by.' },
      { icon: 'icon-palette', title: 'Theme Editor', desc: 'Five rapid taps on the logo opens a hidden editor for accent colour, tier palette, font pairing, corner radius and light/dark mode.' },
    ],
  },
];


// Simple SVG icons for feature cards (avoids emoji encoding issues)
function FeatureIcon({ name }) {
  const s = { width: 18, height: 18, stroke: '#fff', strokeWidth: 2, fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round' };
  const icons = {
    'icon-clock':    <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>,
    'icon-tap':      <svg viewBox="0 0 24 24" {...s}><path d="M9 11.5V6.4a1.7 1.7 0 013.4 0v5.1M12.4 11V9.3a1.5 1.5 0 013 0V11M15.4 11v-.7a1.5 1.5 0 013 0v3.4c0 3.1-2 5.5-5.2 5.5-1.7 0-2.9-.6-3.9-1.8l-2.7-3.3a1.6 1.6 0 012.3-2.2l1.2 1.1"/></svg>,
    'icon-pin':      <svg viewBox="0 0 24 24" {...s}><path d="M12 21s7-5.5 7-11a7 7 0 10-14 0c0 5.5 7 11 7 11z"/><circle cx="12" cy="10" r="2.5"/></svg>,
    'icon-cycle':    <svg viewBox="0 0 24 24" {...s}><path d="M1 4v6h6M23 20v-6h-6"/><path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15"/></svg>,
    'icon-announce': <svg viewBox="0 0 24 24" {...s}><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0"/></svg>,
    'icon-palette':  <svg viewBox="0 0 24 24" {...s}><circle cx="13.5" cy="6.5" r="1.5" fill="#fff"/><circle cx="17.5" cy="10.5" r="1.5" fill="#fff"/><circle cx="8.5" cy="7.5" r="1.5" fill="#fff"/><circle cx="6.5" cy="12" r="1.5" fill="#fff"/><path d="M12 2a10 10 0 000 20 2 2 0 002-2v-.5a2 2 0 012-2h1.5A4.5 4.5 0 0022 12 10 10 0 0012 2z"/></svg>,
    'icon-mic':      <svg viewBox="0 0 24 24" {...s}><rect x="9" y="2" width="6" height="11" rx="3"/><path d="M5 10a7 7 0 0014 0M12 17v4M8 21h8"/></svg>,
    'icon-phone':    <svg viewBox="0 0 24 24" {...s}><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12" y2="18"/></svg>,
    'icon-diamond':  <svg viewBox="0 0 24 24" {...s}><path d="M6 3h12l4 6-10 13L2 9z"/><path d="M2 9h20M12 22L6 9M12 22l6-13"/></svg>,
    'icon-download': <svg viewBox="0 0 24 24" {...s}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>,
    'icon-tag':      <svg viewBox="0 0 24 24" {...s}><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
    'icon-building': <svg viewBox="0 0 24 24" {...s}><rect x="4" y="2" width="16" height="20" rx="1"/><path d="M9 22V12h6v10M9 6h.01M15 6h.01M9 10h.01M15 10h.01"/></svg>,
    'icon-dot':      <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="4" fill="#22c55e" stroke="none"/><circle cx="12" cy="12" r="8"/></svg>,
    'icon-reno':     <svg viewBox="0 0 24 24" {...s}><path d="M2 20h20M5 20V8l7-5 7 5v12"/><path d="M10 20v-6h4v6"/></svg>,
    'icon-bulb':     <svg viewBox="0 0 24 24" {...s}><path d="M9 18h6M10 22h4M12 2a7 7 0 00-4 12.7V17h8v-2.3A7 7 0 0012 2z"/></svg>,
    'icon-edit':     <svg viewBox="0 0 24 24" {...s}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.1 2.1 0 013 3L12 15l-4 1 1-4z"/></svg>,
    'icon-save':     <svg viewBox="0 0 24 24" {...s}><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><path d="M17 21v-8H7v8M7 3v5h8"/></svg>,
    'icon-grid':     <svg viewBox="0 0 24 24" {...s}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
    'icon-swipe':    <svg viewBox="0 0 24 24" {...s}><path d="M5 12h14M15 7l5 5-5 5"/></svg>,
    'icon-zoom':     <svg viewBox="0 0 24 24" {...s}><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35M11 8v6M8 11h6"/></svg>,
    'icon-poster':   <svg viewBox="0 0 24 24" {...s}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>,
    'icon-info':     <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>,
    'icon-star':     <svg viewBox="0 0 24 24" {...s}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z"/></svg>,
  };
  return icons[name] || <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9"/></svg>;
}

function DemosPage({ slug, navigate }) {
  const [activeId, setActiveId] = useStateP(() => {
    if (slug && DEMOS.find(d => d.id === slug)) return slug;
    return DEMOS[0].id;
  });

  // Sync URL when demo changes
  useEffectP(() => {
    const target = '/demos/' + activeId;
    if (window.location.pathname !== target) {
      history.replaceState(null, '', target);
    }
  }, [activeId]);

  // Sync from slug prop (when navigated via link)
  useEffectP(() => {
    if (slug && DEMOS.find(d => d.id === slug)) {
      setActiveId(slug);
    }
  }, [slug]);

  const demo = DEMOS.find(d => d.id === activeId) || DEMOS[0];
  const isPortrait = demo.orientation === 'portrait';

  // Calculate iframe scale to fit viewport
  const containerRef = useRefP(null);
  const [scale, setScale] = useStateP(0.35);
  useEffectP(() => {
    const calc = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const availH = rect.height - 20;
      const availW = rect.width;
      if (isPortrait) {
        // Portrait: scale to fit height, limited by width of left panel
        const maxW = availW * 0.42;
        const sH = availH / 1920;
        const sW = maxW / 1080;
        setScale(Math.min(sH, sW));
      } else {
        // Landscape: scale to fit width
        const maxH = availH * 0.58;
        const sW = availW / 1920;
        const sH = maxH / 1080;
        setScale(Math.min(sH, sW));
      }
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [activeId, isPortrait]);

  const iframeW = isPortrait ? 1080 : 1920;
  const iframeH = isPortrait ? 1920 : 1080;
  const scaledW = iframeW * scale;
  const scaledH = iframeH * scale;

  return (
    <Page name="demos">
      {/* Hero */}
      <section style={{ padding: '100px 0 0' }}>
        <div className="container">
          <div className="eyebrow"><span className="dot"></span>Interactive Demos</div>
          <h1 className="h1" style={{ marginTop: 16, maxWidth: '28ch' }}>
            See it in action<span style={{ color: 'var(--accent)' }}>.</span>
          </h1>
          <p className="lede" style={{ marginTop: 16, maxWidth: '60ch' }}>
            Explore our interactive product demos. Each demo is a fully functional prototype running in real time. Tap, interact and see how our solutions work before you commit.
          </p>
        </div>
      </section>

      {/* Demo selector pills */}
      <section style={{ padding: '32px 0 24px', position: 'sticky', top: 56, zIndex: 20, background: 'var(--bg, #fff)' }}>
        <div className="container">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {DEMOS.map(d => {
              const active = d.id === activeId;
              return (
                <button key={d.id}
                  onClick={() => { setActiveId(d.id); navigate('/demos/' + d.id); }}
                  style={{
                    appearance: 'none', border: 'none', cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 15, fontWeight: 600,
                    padding: '10px 22px', borderRadius: 999,
                    background: active ? 'var(--accent, #0066ff)' : 'var(--surface-2, #f0f0f4)',
                    color: active ? '#fff' : 'var(--text-2, #555)',
                    transition: 'background 180ms, color 180ms, transform 120ms',
                    display: 'flex', alignItems: 'center', gap: 8,
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--surface-3, #e4e4ec)'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'var(--surface-2, #f0f0f4)'; }}
                >
                  <span style={{
                    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                    background: active ? 'rgba(255,255,255,0.6)' : 'var(--text-3, #aaa)',
                    transition: 'background 180ms',
                  }} />
                  {d.name}
                  <span style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                    opacity: 0.6, marginLeft: 2,
                  }}>{d.category}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Demo viewer */}
      <section style={{ padding: '0 0 80px' }}>
        <div className="container" ref={containerRef} style={{
          minHeight: isPortrait ? 680 : 520,
          display: 'flex',
          flexDirection: isPortrait ? 'row' : 'column',
          gap: 0,
          background: 'var(--surface-1, #fafafa)',
          borderRadius: 20,
          border: '1px solid var(--border, rgba(0,0,0,0.06))',
          overflow: 'hidden',
        }}>
          {/* Demo canvas */}
          <div style={{
            flex: isPortrait ? '0 0 auto' : '0 0 auto',
            width: isPortrait ? scaledW + 40 : '100%',
            height: isPortrait ? '100%' : scaledH + 40,
            minHeight: isPortrait ? 680 : scaledH + 40,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #0e1118 0%, #1a1f2e 100%)',
            padding: 20,
            position: 'relative',
          }}>
            {/* Subtle grid bg */}
            <div style={{
              position: 'absolute', inset: 0, opacity: 0.4,
              backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.06) 1px, transparent 0)',
              backgroundSize: '24px 24px',
            }} />



            {/* Iframe container */}
            <div style={{
              width: scaledW, height: scaledH,
              position: 'relative', zIndex: 1,
              borderRadius: 8,
              overflow: 'hidden',
              boxShadow: '0 20px 60px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)',
            }}>
              <iframe
                key={demo.id}
                src={demo.src}
                title={demo.headline}
                style={{
                  border: 'none',
                  width: iframeW,
                  height: iframeH,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  display: 'block',
                }}
              />
            </div>
          </div>

          {/* Info panel */}
          <div style={{
            flex: 1, minWidth: 0,
            display: 'flex', flexDirection: 'column',
            padding: isPortrait ? '36px 36px 36px 32px' : '28px 36px',
            overflowY: 'auto',
          }}>
            {/* Demo header */}
            <div style={{ marginBottom: 24 }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                color: 'var(--accent, #0066ff)',
                background: 'color-mix(in oklab, var(--accent, #0066ff) 10%, transparent)',
                padding: '5px 12px', borderRadius: 999, marginBottom: 12,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
                {demo.category} · {isPortrait ? 'Portrait Kiosk' : 'Landscape Display'}
              </div>
              <h2 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, margin: '0 0 10px' }}>
                {demo.headline}
              </h2>
              <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--text-2, #555)', margin: 0 }}>
                {demo.description}
              </p>
            </div>

            {/* Features guide */}
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: 'var(--text-3, #999)', marginBottom: 12,
            }}>Features & Highlights</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
              {demo.features.map((f, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 14, padding: '14px 16px',
                  background: 'var(--surface-2, #f0f0f4)',
                  borderRadius: 14,
                  border: '1px solid var(--border, rgba(0,0,0,0.04))',
                }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                    background: 'var(--accent, #0066ff)',
                    display: 'grid', placeItems: 'center',
                  }}>
                    <FeatureIcon name={f.icon} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1, #111)', lineHeight: 1.2, marginBottom: 3 }}>{f.title}</div>
                    <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text-2, #555)' }}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div style={{
              marginTop: 24, paddingTop: 20,
              borderTop: '1px solid var(--border, rgba(0,0,0,0.06))',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <a data-link href="/quote" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontSize: 15, fontWeight: 700, color: '#fff',
                background: 'var(--accent, #0066ff)',
                padding: '12px 24px', borderRadius: 999, textDecoration: 'none',
                transition: 'opacity 150ms',
              }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                Get a Quote
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M8 3L13 8L8 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </a>
              <span style={{ fontSize: 14, color: 'var(--text-3, #999)' }}>
                or <a data-link href="/products" style={{ color: 'var(--accent, #0066ff)', textDecoration: 'none', fontWeight: 600 }}>explore products</a>
              </span>
            </div>
          </div>
        </div>
      </section>
    </Page>
  );
}



export { DemosPage, PortfolioPage, HomePage, ProductPage, IndustriesPage, IndustryPage, AboutPage, QuotePage, PricingPage };
